'use client';
import { Button } from '@curology/ui-components-web/react/Button';
import { Typography } from '@curology/ui-components-web/react/Typography';
import clsx from 'clsx';
import Image from 'next/image';

import { useFlowContext } from '@/components/flow/hooks/useFlowContext';
import { type AvailableTimeSlots } from '@/services/liveConsultationService/types';
import { type OptionsType } from '@/services/liveConsultationService/utils';

import { useNavigation } from '../../../../hooks/useNavigation';
import Form from '../form';

const curologyImages = [
  {
    alt: 'Jake',
    src: 'https://images.ctfassets.net/8pzh4g8cs30t/21GkrrouLDkcPvsEbKu2Ws/623f562d767d5a0f21e456affa18abd1/Jake_Swanson_circle.png',
    zIndex: 'cur-z-50',
  },
  {
    alt: 'Erin',
    src: 'https://images.ctfassets.net/8pzh4g8cs30t/7MTLffpbOzlE4jvwF9ptC1/5d8c7c5a622e2ab261ea1222922fb7cb/ErinPate_Circle.png',
    zIndex: 'cur-z-40',
  },
  {
    alt: 'Rebecca',
    src: 'https://images.ctfassets.net/8pzh4g8cs30t/1fwCKgL4aDEpCSRAPWNSZN/67b1bf259d46b981e39a2a0f53e3c988/Rebecca_Navarrete_circle.png',
    zIndex: 'cur-z-30',
  },
];

const agencyImages = [
  {
    alt: 'Provider #1',
    src: 'https://images.ctfassets.net/8pzh4g8cs30t/4bdIUgfxHPXFVPrdsAtmqD/f7bb1a1de34a8b8336cafa2933820dc9/live-consultation-page-avatar-1.png',
    zIndex: 'cur-z-50',
  },
  {
    alt: 'Provider #2',
    src: 'https://images.ctfassets.net/8pzh4g8cs30t/5VOKBF8bUl8Uo7DwUDlSK4/be2e256b0286440d792615a07244b0bb/live-consultation-page-avatar-2.png',
    zIndex: 'cur-z-40',
  },
  {
    alt: 'Provider #3',
    src: 'https://images.ctfassets.net/8pzh4g8cs30t/4q9jonQ2yLsxBTXnLpVWUQ/a94d4464e60fb6e721f6f695339c2ce8/live-consultation-page-avatar-3.png',
    zIndex: 'cur-z-30',
  },
];

const SignupConsultation = ({
  availableTimeSlots,
  dateOptions,
  timeOptions,
}: {
  availableTimeSlots: AvailableTimeSlots;
  dateOptions: OptionsType;
  timeOptions: OptionsType;
}) => {
  const { brand } = useFlowContext();
  const navigation = useNavigation();

  const images = brand === 'agency' ? agencyImages : curologyImages;

  const goBackButton = (
    <Button
      type="button"
      buttonType="text"
      state="dark"
      className="cur-mt-4"
      onClick={navigation.onBack}
    >
      Go back
    </Button>
  );

  return (
    <div className="cur-flex cur-flex-col cur-space-y-12 cur-px-6">
      <Typography size="h1" className="!cur-text-primary cur-text-center">
        Almost there! Set up a quick call with your provider
      </Typography>
      <div className="cur-flex cur-justify-center -cur-space-x-8">
        {images.map((image) => (
          <Image
            className={clsx(
              'cur-relative cur-border-2 cur-border-white cur-rounded-full cur-bg-surface-brand-lightest',
              image.zIndex,
            )}
            key={image.src}
            src={image.src}
            alt={image.alt}
            width={84}
            height={84}
          />
        ))}
      </div>
      {dateOptions.length === 0 ? (
        <Typography size="body" weight="bold" className="cur-text-center">
          All time slots are currently full. Please check again later.
        </Typography>
      ) : (
        <>
          <Typography
            as="p"
            size="body"
            weight="bold"
            className="cur-text-center"
          >
            Meet your provider over the phone to go over a few final details
            before they create your formula. This should only take a few minutes
            👌
          </Typography>
          <Form
            availableTimeSlots={availableTimeSlots}
            dateOptions={dateOptions}
            timeOptions={timeOptions}
            secondaryButton={goBackButton}
          />
        </>
      )}
      <Typography size="meta" className="cur-text-center">
        This call is required by telemedicine laws in your state. We encourage
        you to sign up for the earliest time so you can get started sooner.
      </Typography>
    </div>
  );
};

export default SignupConsultation;
